import type { ADFEntity } from '@atlaskit/adf-utils/types';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
	INPUT_METHOD,
} from '@atlaskit/editor-common/analytics';
import { TEMPLATE_GALLERY_INPUT_SOURCE } from '@atlaskit/editor-common/analytics/types/insert-events';
import { logException } from '@atlaskit/editor-common/monitoring';
import { Fragment } from '@atlaskit/editor-prosemirror/model';
import { TextSelection, type Transaction } from '@atlaskit/editor-prosemirror/state';
import { safeInsert } from '@atlaskit/editor-prosemirror/utils';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

import type {
	InsertSnippetOptions,
	InsertSnippet,
	SnippetsPlugin,
	SnippetsPluginOptions,
} from '../snippetsPluginType';

interface InsertSnippetParams extends InsertSnippetOptions {
	api: Parameters<SnippetsPlugin>[0]['api'];
	config: SnippetsPluginOptions;
	tr: Transaction;
}

const addSnippetContextToMedia = (
	adf: ADFEntity[],
	snippet: InsertSnippetOptions['snippet'],
): { adf: ADFEntity[]; hadMedia: boolean } => {
	const contextId = JSON.stringify({ snippetId: snippet.snippetId, scope: snippet.scope });
	let hadMedia = false;
	const addContext = (node: ADFEntity): ADFEntity => {
		const content = node.content?.map((child) => (child ? addContext(child) : child));
		if (node.type === 'media' || node.type === 'mediaInline') {
			hadMedia = true;
			return {
				...node,
				...(content && { content }),
				attrs: { ...node.attrs, __contextId: contextId },
			};
		}

		return content ? { ...node, content } : node;
	};

	return { adf: adf.map(addContext), hadMedia };
};

const fireInsertFailure = (
	api: Parameters<SnippetsPlugin>[0]['api'],
	snippet: InsertSnippetOptions['snippet'],
	reason: 'emptyBody' | 'parseError' | 'insertError',
): void => {
	api?.analytics?.actions?.fireAnalyticsEvent?.({
		action: ACTION.FAILED_TO_INSERT,
		actionSubject: ACTION_SUBJECT.DOCUMENT,
		actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
		attributes: { reason, snippetId: snippet.snippetId, templateType: snippet.templateType },
		eventType: EVENT_TYPE.OPERATIONAL,
	});
};

const fireInsertSuccess = (
	api: Parameters<SnippetsPlugin>[0]['api'],
	snippet: InsertSnippetOptions['snippet'],
	inputSource: NonNullable<InsertSnippetOptions['inputSource']>,
	hadMedia = false,
): void => {
	api?.analytics?.actions?.fireAnalyticsEvent?.({
		action: ACTION.INSERTED,
		actionSubject: ACTION_SUBJECT.DOCUMENT,
		actionSubjectId: ACTION_SUBJECT_ID.SNIPPET,
		attributes: {
			...(hadMedia && { hadMedia: true }),
			inputMethod: inputSource,
			snippetId: snippet.snippetId,
			templateType: snippet.templateType,
		},
		eventType: EVENT_TYPE.TRACK,
	});
};

const notifyConfiguredCompletion = (
	config: SnippetsPluginOptions,
	inputSource: NonNullable<InsertSnippetOptions['inputSource']>,
	snippet: InsertSnippetOptions['snippet'],
): void => {
	if (inputSource === TEMPLATE_GALLERY_INPUT_SOURCE) {
		return;
	}

	try {
		config.onSnippetInserted?.({ inputMethod: inputSource, snippet });
	} catch (error) {
		logException(error as Error, {
			location: 'editor-plugin-snippets/insertSnippetContent/onSnippetInserted',
		});
	}
};

function insertSnippetContent({
	tr,
	api,
	config,
	inputMethod,
	inputSource,
	onError,
	onSuccess,
	snippet,
}: InsertSnippetParams): Transaction | null {
	const resolvedInputSource = inputSource ?? inputMethod ?? INPUT_METHOD.QUICK_INSERT;
	const adf = snippet.body.content?.filter((node) => node !== undefined);

	if (!adf || adf.length === 0) {
		fireInsertFailure(api, snippet, 'emptyBody');
		logException(new Error('Empty snippet body'), {
			location: 'editor-plugin-snippets/insertSnippetContent',
		});
		return null;
	}

	const preservedSelection = api?.blockControls?.sharedState.currentState()?.preservedSelection;
	const preservedNode = preservedSelection?.$from.doc.nodeAt(preservedSelection.from);
	const preservedEmptyTextBlock =
		preservedSelection &&
		preservedNode?.isTextblock &&
		preservedNode.content.size === 0 &&
		preservedSelection.to === preservedSelection.from + preservedNode.nodeSize;
	const insertPosition = preservedSelection
		? preservedEmptyTextBlock
			? preservedSelection.from + 1
			: (preservedSelection.$from.blockRange(preservedSelection.$to)?.end ?? preservedSelection.to)
		: undefined;
	const finishSuccess = (hadMedia = false): void => {
		fireInsertSuccess(api, snippet, resolvedInputSource, hadMedia);
		notifyConfiguredCompletion(config, resolvedInputSource, snippet);
		try {
			onSuccess?.();
		} catch (error) {
			logException(error as Error, {
				location: 'editor-plugin-snippets/insertSnippetContent/onSuccess',
			});
		}
	};
	const finishError = (reason: 'parseError' | 'insertError', error: unknown): void => {
		fireInsertFailure(api, snippet, reason);
		logException(error as Error, {
			location: 'editor-plugin-snippets/insertSnippetContent',
		});
		try {
			onError?.(reason);
		} catch (callbackError) {
			logException(callbackError as Error, {
				location: 'editor-plugin-snippets/insertSnippetContent/onError',
			});
		}
	};

	const { adf: adfWithMediaContext, hadMedia } = addSnippetContextToMedia(adf, snippet);
	let fragment: Fragment;
	try {
		fragment = Fragment.fromJSON(tr.doc.type.schema, adfWithMediaContext);
	} catch (error) {
		finishError('parseError', error);
		return null;
	}

	let result: Transaction | null = tr;
	try {
		const selection = preservedSelection ?? tr.selection;
		const shouldSplitTopLevelTextBlock =
			selection.empty &&
			selection.$from.depth === 1 &&
			selection.$from.parent.isTextblock &&
			selection.$from.parent.content.size > 0;
		const insertionTr =
			preservedEmptyTextBlock && preservedSelection
				? tr.setSelection(TextSelection.create(tr.doc, preservedSelection.from + 1))
				: tr;
		result = shouldSplitTopLevelTextBlock
			? insertionTr.insert(insertPosition ?? insertionTr.selection.head, fragment)
			: safeInsert(fragment, insertPosition, insertPosition === undefined)(insertionTr);
		if (preservedSelection) {
			result = api?.blockControls?.commands.stopPreservingSelection()({ tr: result }) ?? null;
		}
	} catch (error) {
		finishError('insertError', error);
		return null;
	}
	if (!result) {
		finishError('insertError', new Error('Preserved selection cleanup was not accepted'));
		return null;
	}

	finishSuccess(hadMedia);
	return result;
}

export const insertSnippet =
	(api: Parameters<SnippetsPlugin>[0]['api'], config: SnippetsPluginOptions): InsertSnippet =>
	(options) =>
	({ tr }) => {
		if (!isExperimentEnabled('platform_editor_snippets_plugin_confluence')) {
			return null;
		}

		return insertSnippetContent({ ...options, api, config, tr });
	};
